import { Button } from "@sellar-io/components";
import { useNavigate } from "react-router";
import { COLLECTION_DETAILS, NEW_COLLECTION } from "../../../constants/routes";
import { Alert } from "../../alert/Alert";
import { useCollectionList } from "../../../hooks/api/v2/collections/useCollectionList";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import dayjs from "dayjs";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { useState } from "react";
import { AddOrderToCollectionDialog } from "./AddOrderToCollectionDialog";
import { RemoveOrderFromCollectionDialog } from "./RemoveOrderFromCollectionDialog";

export const OrderCollectionStatus = ({ orderId }: { orderId: number }) => {
  const navigate = useNavigate();
  const [selectedCollection, setSelectedCollection] = useState<number>();
  const [error, setError] = useState<string>();
  const [addCollectionActive, setAddCollectionActive] = useState(false);
  const [removeCollectionActive, setRemoveCollectionActive] = useState(false);
  const orderQuery = useOrder(orderId);

  const collectionListQuery = useCollectionList(
    {
      query: [
        {
          type: "collectionDate",
          modifier: "EQUALS",
          value: orderQuery.data?.dispatchPlannedAt?.toString() ?? "",
        },
        {
          type: "status",
          modifier: "EQUALS",
          value: "active",
        },
        {
          type: "orderId",
          modifier: "EQUALS",
          value: orderId,
        },
      ],
      limit: 10,
      sortBy: "collectionDate",
      sortDir: "desc",
    },
    {
      enabled: !!orderQuery.data,
    },
  );

  const collectionList =
    collectionListQuery.data?.pages.flatMap((page) => page) ?? [];

  if (collectionListQuery.isLoading || orderQuery.isLoading) return;

  const canEdit = (collection: { collectionDate: Date }) => {
    const isBeforeMidday = dayjs().isBefore(dayjs().hour(19));
    const today = dayjs().startOf("day").valueOf();
    const collectionDay = dayjs(collection.collectionDate)
      .startOf("day")
      .valueOf();

    return (collectionDay === today && isBeforeMidday) || collectionDay > today;
  };

  const filteredCollectionList = collectionList.filter((collection) =>
    canEdit(collection),
  );

  const hasCollectionsPlanned = filteredCollectionList.length > 0;

  const collectionWithOrder = collectionList.filter((collection) =>
    collection.Orders.find((order) => order.id === orderId),
  )[0];

  const addOrderToCollection = () => {
    if (!selectedCollection) return setError("Please select a collection");
    setError(undefined);

    setAddCollectionActive(true);
  };

  const canEditOrderCollection = collectionWithOrder
    ? canEdit(collectionWithOrder)
    : false;

  if (!!collectionWithOrder)
    return (
      <>
        <Alert
          className={"w-full"}
          message={
            canEditOrderCollection
              ? "Do you need to make any changes?"
              : "This collection can no longer be edited."
          }
          title={`Collection planned - ${dayjs(collectionWithOrder.collectionDate).format("DD/MM/YYYY")}`}
          type={"success"}
        >
          <div className="flex gap-2">
            <Button
              size="small"
              onClick={() =>
                navigate(
                  COLLECTION_DETAILS.href({
                    collectionId: collectionWithOrder.id,
                  }),
                )
              }
            >
              Go To Collection
            </Button>
            {canEditOrderCollection && (
              <Button
                color={"transparent"}
                size="small"
                onClick={() => setRemoveCollectionActive(true)}
              >
                Remove From Collection
              </Button>
            )}
          </div>
        </Alert>
        <RemoveOrderFromCollectionDialog
          active={removeCollectionActive}
          onDismiss={() => setRemoveCollectionActive(false)}
          orderId={orderId}
          collectionId={collectionWithOrder.id}
        />
      </>
    );

  if (hasCollectionsPlanned)
    return (
      <>
        <Alert
          className={"w-full"}
          message={
            "You already have a collection planned for this order's dispatch date. Would you like to add this order to that collection?"
          }
          title={"Add this order to your planned collection"}
          type={"information"}
        >
          <div className="flex flex-col gap-2">
            <InputContainer className="h-[36px] max-w-xs">
              <InputSelect
                value={selectedCollection}
                onChange={(e) =>
                  setSelectedCollection(parseInt(e.target.value))
                }
              >
                <InputOption value="">-- Select collection --</InputOption>
                {collectionList.map((collection, idx) => (
                  <InputOption key={idx} value={collection.id}>
                    {dayjs(collection.collectionDate).format("DD/MM/YYYY")}
                  </InputOption>
                ))}
              </InputSelect>
            </InputContainer>
            {!!error && <p className="text-red-500">{error}</p>}
          </div>
          <div className="flex gap-2">
            <Button size="small" onClick={() => addOrderToCollection()}>
              Add To Collection
            </Button>
            <Button
              color={"transparent"}
              size="small"
              onClick={() => navigate(NEW_COLLECTION, { state: { orderId } })}
            >
              Book New Collection
            </Button>
          </div>
        </Alert>
        <AddOrderToCollectionDialog
          active={addCollectionActive}
          onDismiss={() => setAddCollectionActive(false)}
          orderId={orderId}
          collectionId={selectedCollection ?? 0}
        />
      </>
    );

  return (
    <Alert
      className={"w-full"}
      message={
        "You will need to schedule a collection. You can include multiple shipments in a single collection. If you have already done this, please ignore this message."
      }
      title={"Have you booked your collection?"}
      type={"information"}
    >
      <Button
        size="small"
        onClick={() => navigate(NEW_COLLECTION, { state: { orderId } })}
      >
        Book Collection
      </Button>
    </Alert>
  );
};
